import React from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputSwitch } from "primereact/inputswitch";
import { Toast } from "primereact/toast";
import { Message } from "primereact/message";
import agent from "../../../agent";
import RuleDialog from "./RuleDialog";
import { groupByOrg } from "./groupByOrg";
import { MILE_TYPES } from "../TariffPlans/mileTypes";

/**
 * Скидки и тарифные карты партнёров — nsi.partner_discount в market-service.
 *
 * Настройки задаются по плечам доставки: на каждом плече партнёру можно назначить
 * тарифную карту, скидку или и то и другое. Приоритет у карты: покрыла она
 * направление — цена из карты без скидки, не покрыла — обычный расчёт со скидкой
 * этого плеча.
 *
 * Всё это попадает прямо в цены плеч маршрута, поэтому одинаково видно и в счёте
 * на slq.kz, и в заказе в opost, и в базе агентского вознаграждения. Правка
 * действует без деплоя и рестарта сервисов.
 */
const PartnerDiscounts = () => {
    const toast = React.useRef(null);

    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);

    const [rows, setRows] = React.useState([]);
    const [orgs, setOrgs] = React.useState([]);
    const [plans, setPlans] = React.useState([]);

    const [dialogVisible, setDialogVisible] = React.useState(false);
    const [editRule, setEditRule] = React.useState(null);

    const showError = (detail) => toast.current?.show({ severity: "error", summary: "Ошибка", detail, life: 6000 });
    const showSuccess = (detail) => toast.current?.show({ severity: "success", summary: "Успешно", detail, life: 3000 });

    React.useEffect(() => {
        agent.Organizations.all()
            .then(res => setOrgs(Array.isArray(res) ? res : (res?.content || [])))
            .catch(() => {});
        agent.TariffPlans.list()
            .then(res => setPlans(Array.isArray(res) ? res : []))
            .catch(() => {});
    }, []);

    const load = React.useCallback(() => {
        setLoading(true);
        agent.PartnerDiscounts.list()
            .then(res => setRows(groupByOrg(Array.isArray(res) ? res : (res?.content || []))))
            .catch(err => showError(readError(err)))
            .finally(() => setLoading(false));
    }, []);

    React.useEffect(() => { load(); }, [load]);

    /**
     * Сервер хранит строку на плечо, поэтому сохранение расходится на четыре
     * запроса: заданное плечо пишется, очищенное — удаляется. Пустая строка
     * «без процента и без карты» не хранится: она ничего не делает.
     */
    const onSave = (data) => {
        setSaving(true);
        const existing = rows.find(r => r.orgId === data.orgId);

        const calls = MILE_TYPES.map(({ value: mileType }) => {
            const mile = data.miles[mileType] ?? {};
            const hasSettings =
                mile.percent != null || mile.tariffPlanId != null || data.trustRequestPercent;

            if (hasSettings) {
                return agent.PartnerDiscounts.save({
                    orgId: data.orgId,
                    mileType,
                    percent: mile.percent ?? null,
                    tariffPlanId: mile.tariffPlanId ?? null,
                    trustRequestPercent: data.trustRequestPercent,
                    active: data.active,
                });
            }
            // Удаляем только то, что было: иначе получим 400 на пустом плече.
            return existing?.miles?.[mileType]
                ? agent.PartnerDiscounts.remove(data.orgId, mileType)
                : Promise.resolve();
        });

        Promise.all(calls)
            .then(() => {
                showSuccess("Настройки сохранены, новые цены действуют сразу");
                setDialogVisible(false);
                setEditRule(null);
                load();
            })
            .catch(err => showError(readError(err)))
            .finally(() => setSaving(false));
    };

    const onToggleActive = (row, newActive) => {
        setRows(prev => prev.map(r => r.orgId === row.orgId ? { ...r, active: newActive } : r));
        agent.PartnerDiscounts.setActive(row.orgId, newActive)
            .then(() => showSuccess(`Настройки партнёра ${newActive ? "включены" : "выключены"}`))
            .catch(err => {
                setRows(prev => prev.map(r => r.orgId === row.orgId ? { ...r, active: !newActive } : r));
                showError(readError(err));
            });
    };

    const mileBody = (row, mileType) => {
        const mile = row.miles?.[mileType];
        if (!mile) return <span style={{ color: "#9ca3af" }}>—</span>;

        const plan = plans.find(p => p.id === mile.tariffPlanId);
        return (
            <div>
                {mile.tariffPlanId
                    ? <div>{plan?.name ?? `карта #${mile.tariffPlanId}`}</div>
                    : <div style={{ color: "#6b7280" }}>стандартный тариф</div>}
                {mile.percent == null
                    ? <div style={{ color: "#9ca3af" }}>без скидки</div>
                    : <div title={mile.tariffPlanId ? "Сработает на направлениях, которых нет в карте" : undefined}>
                        −{Number(mile.percent)}%
                    </div>}
            </div>
        );
    };

    const trustBody = (row) =>
        row.trustRequestPercent
            ? <span title="Процент берётся из запроса — так работает калькулятор slq.kz">да</span>
            : "нет";

    return (
        <div>
            <Toast ref={toast} />

            <div className="p-d-flex p-jc-between p-ai-center">
                <h1>Скидки и тарифные карты партнёров</h1>
                <Button className="p-d-block" label="Настроить партнёра" icon="pi pi-plus"
                        onClick={() => { setEditRule(null); setDialogVisible(true); }} />
            </div>

            <Message severity="info" style={{ display: "block", marginBottom: 10 }}
                     text="Настройки задаются по плечам. Приоритет у тарифной карты: покрыла она направление — цена из карты, не покрыла — обычный расчёт со скидкой этого плеча. Цена из карты финальная: наценка платформы поверх неё не начисляется. ПРЗ, ПРД и страховка не дисконтируются." />

            <DataTable value={rows} loading={loading} dataKey="orgId"
                       emptyMessage="Настроенных партнёров пока нет">
                <Column field="companyName" header="Организация" style={{ width: "220px" }} />
                {MILE_TYPES.map(({ value, label }) => (
                    <Column key={value} header={label} body={(row) => mileBody(row, value)} />
                ))}
                <Column header="Процент из запроса" body={trustBody} style={{ width: "150px" }} />
                <Column header="Включено" style={{ width: "110px" }}
                        body={(row) => <InputSwitch checked={!!row.active}
                                                    onChange={(e) => onToggleActive(row, e.value)} />} />
                <Column header="" style={{ width: "70px" }}
                        body={(row) => <Button icon="pi pi-pencil" className="p-button-rounded p-button-text"
                                               onClick={() => { setEditRule(row); setDialogVisible(true); }} />} />
            </DataTable>

            <RuleDialog visible={dialogVisible} loading={saving} rule={editRule} orgs={orgs} plans={plans}
                        onHide={() => { setDialogVisible(false); setEditRule(null); }}
                        onSubmit={onSave} />
        </div>
    );
};

const readError = (err) =>
    (typeof err === "string" ? err : null)
    || err?.response?.body?.messages?.[0]
    || err?.response?.body?.message
    || agent.handleError(err);

export default PartnerDiscounts;
